import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import AdminMaintenanceCard from "@/components/AdminMaintenanceCard";
import { api } from "@/lib/api";
import {
  ShieldCheck,
  ShieldOff,
  ArrowRight,
  Crown,
  Users as UsersIcon,
  CalendarClock,
  Inbox,
  CalendarDays,
  TrendingUp,
  ExternalLink,
} from "lucide-react";

function MetricCard({ label, value, hint, to, testid }) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
          {label}
        </span>
        {to && <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition" />}
      </div>
      <div className="font-display text-4xl mt-3 text-foreground" data-testid={`${testid}-value`}>
        {value ?? "—"}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-2">{hint}</p>}
    </>
  );
  const cls =
    "group border border-border rounded-xl bg-card p-5 hover:border-primary/40 transition";
  return to ? (
    <Link to={to} className={cls} data-testid={testid}>
      {inner}
    </Link>
  ) : (
    <div className={cls} data-testid={testid}>
      {inner}
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/dashboard/metrics")
      .then((r) => mounted && setMetrics(r.data))
      .catch(() => {})
      .finally(() => mounted && setLoadingMetrics(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-dashboard">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">Console</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">
              Welcome back<span className="gold-text">.</span>
            </h1>
            <p className="text-muted-foreground mt-3 text-sm">
              Signed in as <span className="text-foreground">{user?.email}</span>
              {" · "}
              <span className="uppercase tracking-widest text-[10px] text-primary">{user?.role}</span>
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
            data-testid="dashboard-view-site"
          >
            View public site <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {isSuper && <AdminMaintenanceCard />}

        <section aria-labelledby="metrics-heading">
          <h2 id="metrics-heading" className="sr-only">
            Key metrics
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Bookings today"
              value={loadingMetrics ? "…" : metrics?.bookings_today}
              hint="Pending + confirmed for today"
              to="/admin/bookings"
              testid="metric-bookings-today"
            />
            <MetricCard
              label="Pending total"
              value={loadingMetrics ? "…" : metrics?.pending_total}
              hint="Awaiting confirmation"
              to="/admin/bookings?status=pending"
              testid="metric-pending-total"
            />
            <MetricCard
              label="Unread messages"
              value={loadingMetrics ? "…" : metrics?.unread_messages}
              hint="Contact inbox"
              to="/admin/contact"
              testid="metric-unread-messages"
            />
            <MetricCard
              label="Upcoming events"
              value={loadingMetrics ? "…" : metrics?.upcoming_events}
              hint="Published & in future"
              to="/admin/events"
              testid="metric-upcoming-events"
            />
          </div>
        </section>

        <div className="grid sm:grid-cols-2 gap-5">
          <Link
            to="/admin/profile"
            className="group border border-border hover:border-primary/40 rounded-xl p-6 bg-card transition"
            data-testid="dashboard-profile-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {user?.totp_enabled ? (
                  <ShieldCheck className="w-5 h-5 text-primary" />
                ) : (
                  <ShieldOff className="w-5 h-5 text-amber-400" />
                )}
                <h3 className="font-display text-xl">Your profile</h3>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition" />
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {user?.totp_enabled
                ? "2FA is active. Manage backup codes or disable from your profile."
                : "2FA is disabled — enable it to protect your admin account."}
            </p>
          </Link>

          {isSuper ? (
            <Link
              to="/admin/users"
              className="group border border-border hover:border-primary/40 rounded-xl p-6 bg-card transition"
              data-testid="dashboard-users-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <UsersIcon className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-xl">Users & audit</h3>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition" />
              </div>
              <p className="text-sm text-muted-foreground mt-3">
                Create admins, force-disable 2FA, and review the audit log.
              </p>
            </Link>
          ) : (
            <div className="border border-border rounded-xl p-6 bg-card opacity-60">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-primary/60" />
                <h3 className="font-display text-xl">Users & audit</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-3">Superadmin-only area.</p>
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Link
            to="/admin/bookings"
            className="group border border-border hover:border-primary/40 rounded-xl p-5 bg-card transition"
            data-testid="dashboard-quick-bookings"
          >
            <CalendarClock className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg mt-3">Bookings</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.bookings_this_week ?? 0} this week
            </p>
          </Link>
          <Link
            to="/admin/contact"
            className="group border border-border hover:border-primary/40 rounded-xl p-5 bg-card transition"
            data-testid="dashboard-quick-contact"
          >
            <Inbox className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg mt-3">Contact inbox</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.unread_messages ?? 0} unread
            </p>
          </Link>
          <Link
            to="/admin/events"
            className="group border border-border hover:border-primary/40 rounded-xl p-5 bg-card transition"
            data-testid="dashboard-quick-events"
          >
            <CalendarDays className="w-5 h-5 text-primary" />
            <h3 className="font-display text-lg mt-3">Events</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics?.upcoming_events ?? 0} upcoming
            </p>
          </Link>
        </div>

        {isSuper && (
          <div className="border border-dashed border-border rounded-xl p-5 text-sm text-muted-foreground flex items-center gap-3">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span>
              Need Stripe or Email diagnostics? Open{" "}
              <Link
                to="/admin/booking-settings"
                className="text-primary hover:underline"
                data-testid="dashboard-diagnostics-link"
              >
                Booking settings → Diagnostics
              </Link>
              .
            </span>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
